import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, getErrorMessage } from '../api/client';
import {
  beginZettleAuthorization,
  completeZettleAuthorization,
  disconnectZettle,
  getZettleConnection,
  ZETTLE_DISCONNECT_CONFIRMATION,
  type ZettleConnection,
} from '../api/paymentProviders';
import { readZettleSessionIdentity, type ZettleSessionIdentity } from '../native/zettlePayments';
import { parseZettleCallbackHash } from '../utils/zettleOAuthCallback';
import { describeZettleDeviceSession } from '../utils/zettlePos';

/**
 * PayPal/Zettle LIVE readiness (L-05 / L-12): la conexión OAuth con Zettle, como la vive el Dueño.
 *
 *   Conectar → el backend da la URL de autorización → el navegador NAVEGA a Zettle → Zettle regresa a
 *   `/zettle-callback.html?code&state` → esa página lo pasa a `#integrations/zettle-callback?…` → esta
 *   tarjeta lo reenvía al backend CON la sesión y borra el code de la barra de direcciones.
 *
 * El navegador nunca recibe un token. Conectar NO habilita cobros con dinero real: eso lo decide
 * Negocio360 cuenta por cuenta (candado LIVE), y la tarjeta solo lo informa. Con la reconciliación
 * apagada en el servidor, la tarjeta lo dice y no ofrece ninguna acción.
 */

type Availability = 'loading' | 'disabled' | 'ready' | 'error';
type Busy = 'connect' | 'callback' | 'disconnect' | null;

export function ZettleConnectionCard() {
  const [availability, setAvailability] = useState<Availability>('loading');
  const [connection, setConnection] = useState<ZettleConnection | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [confirmText, setConfirmText] = useState('');
  // Deuda (n): con qué cuenta de Zettle está la sesión del SDK en ESTE equipo. Diagnóstico para el
  // dueño, no una defensa: quien impide cobrar en la cuenta equivocada es el servidor.
  const [session, setSession] = useState<ZettleSessionIdentity>();
  const startedRef = useRef(false);

  const load = useCallback(async () => {
    // En su propio try/catch: un fallo del puente jamás puede romper la tarjeta de la conexión.
    try {
      setSession(await readZettleSessionIdentity());
    } catch {
      setSession(undefined);
    }
    try {
      setConnection(await getZettleConnection());
      setAvailability('ready');
    } catch (err) {
      if (err instanceof ApiError && err.payload?.error === 'PAYMENT_RECONCILIATION_DISABLED') {
        setAvailability('disabled');
        return;
      }
      setAvailability('error');
      setError(getErrorMessage(err, 'No fue posible consultar la conexión con Zettle.'));
    }
  }, []);

  useEffect(() => {
    // El regreso de Zettle se procesa UNA vez (en desarrollo React monta los efectos dos veces).
    if (startedRef.current) return;
    startedRef.current = true;

    const result = parseZettleCallbackHash(window.location.hash);
    if (result.kind === 'none') {
      void load();
      return;
    }
    // El code no debe quedarse ni en la barra de direcciones ni en el historial.
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#integrations`);

    if (result.kind === 'denied') {
      setError('La conexión se canceló en Zettle. No se guardó nada.');
      void load();
      return;
    }
    if (result.kind === 'invalid') {
      setError('El regreso de Zettle no traía los datos esperados. Vuelve a conectar.');
      void load();
      return;
    }

    setBusy('callback');
    completeZettleAuthorization(result.code, result.state)
      .then(() => setNotice('Cuenta de Zettle conectada. Para cobrar con dinero real, Negocio360 todavía debe habilitar LIVE en esta cuenta.'))
      .catch((err: unknown) => setError(getErrorMessage(err, 'No fue posible completar la conexión con Zettle.')))
      .finally(() => {
        setBusy(null);
        void load();
      });
  }, [load]);

  const connect = async () => {
    setError(undefined);
    setNotice(undefined);
    setBusy('connect');
    try {
      const { authorizeUrl } = await beginZettleAuthorization();
      window.location.assign(authorizeUrl);
    } catch (err) {
      setError(getErrorMessage(err, 'No fue posible iniciar la conexión con Zettle.'));
      setBusy(null);
    }
  };

  const disconnect = async () => {
    setError(undefined);
    setNotice(undefined);
    setBusy('disconnect');
    try {
      const result = await disconnectZettle(confirmText.trim());
      setConfirmText('');
      setNotice(result.providerDisconnected
        ? 'Cuenta de Zettle desconectada.'
        : 'Cuenta desconectada en Negocio360. Zettle no confirmó la desconexión: retira también la app desde tu cuenta de Zettle.');
    } catch (err) {
      setError(getErrorMessage(err, 'No fue posible desconectar la cuenta de Zettle.'));
    } finally {
      setBusy(null);
      void load();
    }
  };

  const account = connection?.account ?? null;
  const realAccount = account !== null && !account.developerMode;
  const pill = statusPill(availability, connection);

  return (
    <article className="admin-card integration-status-card">
      <div className="integration-card-heading">
        <div><span className="eyebrow">Cobros con lector</span><h2>Conexión Zettle</h2></div>
        <span className={`status-pill ${pill.className}`}>{pill.label}</span>
      </div>

      {error && <div className="alert alert--error">{error}</div>}
      {notice && <div className="alert alert--success">{notice}</div>}

      {(availability === 'loading' || busy === 'callback') && (
        <div className="inline-loading"><span className="spinner" />{busy === 'callback' ? 'Completando la conexión con Zettle...' : 'Consultando la conexión...'}</div>
      )}

      {availability === 'disabled' && (
        <p className="integration-scope-note">La conexión con Zettle todavía no está habilitada en este servidor.</p>
      )}

      {availability === 'ready' && connection && busy !== 'callback' && (
        <>
          <dl className="detail-list integration-details">
            <div><dt>Cuenta</dt><dd>{accountLabel(connection)}</dd></div>
            <div>
              <dt>Sesión del lector en este equipo</dt>
              <dd className={`device-session device-session--${describeZettleDeviceSession(connection.account?.externalAccountId ?? null, session).tone}`}>
                {describeZettleDeviceSession(connection.account?.externalAccountId ?? null, session).text}
              </dd>
            </div>
            <div><dt>Credencial</dt><dd>{credentialLabel(connection)}</dd></div>
            <div><dt>Cobro con dinero real</dt><dd>{connection.liveEnabled ? 'Habilitado por Negocio360' : 'No habilitado (lo habilita Negocio360 después de la prueba controlada)'}</dd></div>
          </dl>

          {!connection.connected && !account?.developerMode && (
            <div className="integration-actions">
              <button className="button button--primary" disabled={busy !== null} onClick={() => void connect()} type="button">
                {busy === 'connect' ? 'Abriendo Zettle...' : account ? 'Volver a conectar' : 'Conectar con Zettle'}
              </button>
            </div>
          )}

          {realAccount && (
            <div className="integration-actions">
              <label className="field">
                <span>Para desconectar, escribe {ZETTLE_DISCONNECT_CONFIRMATION}</span>
                <input autoComplete="off" disabled={busy !== null} onChange={(event) => setConfirmText(event.target.value)} value={confirmText} />
              </label>
              <button
                className="button button--ghost integration-danger"
                disabled={busy !== null || confirmText.trim() !== ZETTLE_DISCONNECT_CONFIRMATION}
                onClick={() => void disconnect()}
                type="button"
              >
                {busy === 'disconnect' ? 'Desconectando...' : 'Desconectar'}
              </button>
            </div>
          )}
        </>
      )}
    </article>
  );
}

function statusPill(availability: Availability, connection: ZettleConnection | null): { label: string; className: string } {
  if (availability === 'disabled') return { label: 'No disponible', className: 'status-pill--inactive' };
  if (!connection) return { label: '—', className: 'status-pill--neutral' };
  if (connection.connected) return { label: 'Conectada', className: '' };
  if (connection.account) return { label: 'Requiere reconectar', className: 'status-pill--neutral' };
  return { label: 'Sin conectar', className: 'status-pill--inactive' };
}

function accountLabel(connection: ZettleConnection): string {
  if (!connection.account) return 'Sin cuenta conectada';
  if (connection.account.developerMode) return 'Cuenta de pruebas (Developer Mode)';
  return `Organización …${connection.account.externalAccountId.slice(-8)}`;
}

function credentialLabel(connection: ZettleConnection): string {
  const credential = connection.credential;
  if (!credential) return 'Sin credencial';
  if (credential.status === 'ACTIVE') return `Vigente · termina en ${credential.tokenLastFour}`;
  if (credential.status === 'REAUTH_REQUIRED') return 'Zettle ya no acepta la autorización: vuelve a conectar';
  return 'Revocada';
}
